import React, { useState } from 'react';

import { ApolloError } from '@apollo/client';

import { Button, Text } from '@shared/components/bootstrap';

import { IInvoiceActionFormProps } from '@admin/components/billing/invoices/types';
import { InputFormGroup } from '@admin/components/fields';
import { client } from '@admin/libraries/apollo';
import { useBillingInvoiceReBillMutation } from '@admin/schema';
import { pollForInvoiceEvent } from '@admin/utils/poll_for_invoice_event';

import { ReasonSelector } from './reason_selector';

export const InvoiceRebillForm: React.FC<IInvoiceActionFormProps> = ({ invoiceID, accountID, onSave, onCancel }) => {
  const [notes, setNotes] = useState<string | undefined>();
  const [reasonID, setReasonID] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [polling, setPolling] = useState<boolean>(false);

  const [rebill, { loading }] = useBillingInvoiceReBillMutation({ client });

  const disabled = !notes || !reasonID;

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (loading || !notes || !reasonID) {
      return;
    }

    setError(undefined);
    try {
      const response = await rebill({
        variables: {
          invoiceID,
          input: {
            notes,
            reasonID,
          },
        },
      });

      const attemptID = response.data?.rebill?.attemptID;
      if (!attemptID) {
        setError('Unknown error occurred. Please try again later.');
        return;
      }

      setPolling(true);
      try {
        await pollForInvoiceEvent(accountID, attemptID);
      } finally {
        setPolling(false);
      }
      onSave(attemptID);
    } catch (e: unknown) {
      const message =
        e instanceof ApolloError
          ? e.graphQLErrors.map((err) => err.message).join(' ') || e.message
          : e instanceof Error
            ? e.message
            : 'Request failed. Please try again.';
      setError(message);
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <ReasonSelector reasonID={reasonID} onSelect={setReasonID} invoiceID={invoiceID} />
      <InputFormGroup
        id="re_bill_notes"
        label="Notes:"
        type="text"
        placeholder="Notes"
        value={notes || ''}
        onChange={(event) => setNotes(event.target.value || undefined)}
      />
      {error && (
        <Text tag="p" style="danger" alignment="center">
          <strong>{error}</strong>
        </Text>
      )}
      <Text alignment="right" tag="div">
        <Button kind="danger" disabled={loading || polling} onClick={onCancel}>
          Cancel
        </Button>{' '}
        <Button kind="primary" type="submit" loading={loading || polling} disabled={disabled}>
          Re-Bill
        </Button>
      </Text>
    </form>
  );
};
